import { RICH_EDITOR_COLOR_THEME_ADAPTED_CLASS } from './textEditorBodyUtils';

const SKIP_INLINE_TAGS = new Set(['TABLE', 'TBODY', 'THEAD', 'TFOOT', 'TR', 'COL', 'COLGROUP']);

function isTransparentColor(value) {
  return !value || value === 'transparent' || value === 'rgba(0, 0, 0, 0)';
}

function setStyleIfValue(el, prop, value, { force = false } = {}) {
  if (value == null || value === '') return;
  if (prop === 'backgroundColor' && isTransparentColor(value)) return;
  if (prop === 'textDecoration' && (value === 'none' || value.startsWith('none '))) return;
  const kebab = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
  if (!force && el.style.getPropertyValue(kebab)) return;
  el.style.setProperty(kebab, value);
}

function applyExportComputedStyles(el) {
  if (!(el instanceof HTMLElement)) return;
  if (SKIP_INLINE_TAGS.has(el.tagName)) return;

  const themeAdapted = el.classList.contains(RICH_EDITOR_COLOR_THEME_ADAPTED_CLASS);
  if (themeAdapted) {
    el.classList.remove(RICH_EDITOR_COLOR_THEME_ADAPTED_CLASS);
  }

  const cs = window.getComputedStyle(el);

  if (el.tagName === 'FONT' && el.getAttribute('color') && !el.style.color) {
    el.style.color = el.getAttribute('color');
  }

  setStyleIfValue(el, 'color', cs.color, {
    force: themeAdapted || !el.style.getPropertyValue('color'),
  });
  setStyleIfValue(el, 'backgroundColor', cs.backgroundColor);
  setStyleIfValue(el, 'fontSize', cs.fontSize);
  setStyleIfValue(el, 'fontWeight', cs.fontWeight);
  setStyleIfValue(el, 'fontStyle', cs.fontStyle);
  setStyleIfValue(el, 'fontFamily', cs.fontFamily);
  setStyleIfValue(el, 'textDecoration', cs.textDecoration);
  setStyleIfValue(el, 'textAlign', cs.textAlign);

  if (el.tagName === 'TD' || el.tagName === 'TH' || el.tagName === 'SUB' || el.tagName === 'SUP') {
    setStyleIfValue(el, 'verticalAlign', cs.verticalAlign);
  }

  if (el.tagName === 'TD' || el.tagName === 'TH') {
    if (cs.borderTopWidth !== '0px') {
      setStyleIfValue(el, 'border', cs.border);
    }
  }
}

/**
 * Mount sanitized HTML beside the live editor so computed styles match the UI, then
 * inline those styles for Word / PDF / DOCX export.
 */
export function mountAndInlinePresentationStyles(html, surfaceClassName, parentEl) {
  if (!html || !parentEl || typeof document === 'undefined') return html || '';

  const mount = document.createElement('div');
  if (surfaceClassName) mount.className = surfaceClassName;
  mount.innerHTML = html;
  mount.setAttribute('contenteditable', 'false');
  mount.setAttribute('aria-hidden', 'true');
  mount.style.cssText = [
    'position:absolute',
    'left:0',
    'top:0',
    'width:100%',
    'max-width:100%',
    'visibility:hidden',
    'pointer-events:none',
    'height:auto',
    'overflow:visible',
  ].join(';');

  parentEl.appendChild(mount);

  try {
    const nodes = [mount, ...mount.querySelectorAll('*')];
    nodes.forEach(applyExportComputedStyles);
    return mount.innerHTML;
  } finally {
    mount.remove();
  }
}

/**
 * Inline presentation styles on exported HTML so Word / print / PDF capture
 * tables, lists, and typography without relying on editor-only CSS.
 */
export function enrichHtmlForDocumentExport(html) {
  if (!html || typeof document === 'undefined') return html || '';
  const root = document.createElement('div');
  root.innerHTML = html;

  root.querySelectorAll('table').forEach((table) => {
    table.style.borderCollapse = 'collapse';
    table.style.width = table.style.width || '100%';
    if (!table.getAttribute('border')) table.setAttribute('border', '1');
    if (!table.style.border) table.style.border = '1px solid #94a3b8';
    table.querySelectorAll('th, td').forEach((cell) => {
      if (!cell.style.border) cell.style.border = '1px solid #94a3b8';
      if (!cell.style.padding) cell.style.padding = '6px 8px';
      cell.style.verticalAlign = 'top';
    });
    table.querySelectorAll('th').forEach((th) => {
      th.style.fontWeight = '600';
      if (!th.style.backgroundColor) th.style.backgroundColor = '#f1f5f9';
    });
  });

  root.querySelectorAll('ul').forEach((ul) => {
    ul.style.listStyleType = ul.style.listStyleType || 'disc';
    ul.style.paddingLeft = ul.style.paddingLeft || '1.5rem';
    ul.style.margin = ul.style.margin || '0.5rem 0';
  });

  root.querySelectorAll('ol').forEach((ol) => {
    ol.style.listStyleType = ol.style.listStyleType || 'decimal';
    ol.style.paddingLeft = ol.style.paddingLeft || '1.5rem';
    ol.style.margin = ol.style.margin || '0.5rem 0';
  });

  root.querySelectorAll('li').forEach((li) => {
    li.style.display = li.style.display || 'list-item';
    li.style.margin = li.style.margin || '0.25rem 0';
  });

  root.querySelectorAll('blockquote').forEach((el) => {
    el.style.borderLeft = el.style.borderLeft || '3px solid #cbd5e1';
    el.style.margin = el.style.margin || '0.75rem 0';
    el.style.paddingLeft = el.style.paddingLeft || '0.75rem';
  });

  root.querySelectorAll('pre').forEach((el) => {
    el.style.fontFamily = el.style.fontFamily || 'Consolas, "Courier New", monospace';
    el.style.fontSize = el.style.fontSize || '10pt';
    el.style.background = el.style.background || '#f8fafc';
    el.style.padding = el.style.padding || '0.65rem 0.75rem';
    el.style.borderRadius = el.style.borderRadius || '6px';
    el.style.overflowX = 'auto';
  });

  ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach((tag) => {
    root.querySelectorAll(tag).forEach((el) => {
      el.style.fontWeight = el.style.fontWeight || '600';
      el.style.margin = el.style.margin || '0.75rem 0 0.35rem';
    });
  });

  root.querySelectorAll('p').forEach((p) => {
    if (!p.style.margin) p.style.margin = '0 0 0.5rem';
  });

  root.querySelectorAll('hr').forEach((hr) => {
    hr.style.border = '0';
    hr.style.borderTop = '1px solid #cbd5e1';
    hr.style.margin = '0.75rem 0';
  });

  return root.innerHTML;
}
